import { reaction } from 'mobx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HTMLMessage } from '../models';
import { useStores } from '../stores';
import { MESSAGE_TYPE_RECEIVED } from '../utilities';
import useConfig from './useConfig';

/**
 * @todo move to store
 */
export default function useWelcomeMessage() {
  const {
    message: { welcomeMessage },
    intentOnInit,
    headerMenu,
    projectSelector
  } = useConfig();
  const { app, chat, chatWindow, network, ui } = useStores();
  const { t } = useTranslation();
  const isSent = useRef(false);
  const [isForced, setForced] = useState(false);

  useEffect(
    () =>
      reaction(() => welcomeMessage && chat.messages.length === 0, setForced, {
        delay: welcomeMessage ? welcomeMessage.timeout : 300
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [welcomeMessage]
  );

  useEffect(() => {
    if (chat.isSpecialMessage || (intentOnInit && intentOnInit.enabled)) {
      return () => {};
    }

    if (!welcomeMessage || ui.isStartChatWithoutCategory) {
      chat.isWelcomeMessage = true;
      return () => {};
    }

    if (chatWindow.isCookie) {
      return () => {};
    }

    let timeoutId;
    const isChatReady = chatWindow.isOpened && network.isOnline && !network.isSlowConnection;

    function handleStart(id, projectId, source, evt) {
      evt.preventDefault();
      const parent = document.getElementById('messages-content');
      const childs = parent.childNodes;
      childs.forEach(child => {
        child.innerHTML = '';
      });
      if (id === 1) {
        chat.addMessage(
          new HTMLMessage({
            content: t('projectSelector.subMessage1', {
              escapeValue: false
            }),
            type: MESSAGE_TYPE_RECEIVED
          })
        );
      } else if (id === 2) {
        chat.addMessage(
          new HTMLMessage({
            content: t('projectSelector.subMessage2', {
              escapeValue: false
            }),
            type: MESSAGE_TYPE_RECEIVED
          })
        );
      }
      ui.setMessageInputDisabled(false);
      ui.setProjectId(id, projectId, source);
    }

    if (isChatReady && (isForced || !isSent.current) && app.isReadyLanguage) {
      timeoutId = setTimeout(() => {
        if (headerMenu?.enabled && ui.category.id) {
          if (!ui.category.isCleared) {
            chat.addMessage(
              new HTMLMessage({
                content: t('headerMenu.welcomeMessage', {
                  escapeValue: false,
                  categoryName: ui.category.id ? t(ui.category.name) : null
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );
          }
        } else if (projectSelector?.enabled) {
          if (
            window.location.href.toLowerCase().indexOf(projectSelector.detectURL1) <= 0 &&
            window.location.href.toLowerCase().indexOf(projectSelector.detectURL2) <= 0
          ) {
            ui.setMessageInputDisabled(true);
            chat.addMessage(
              new HTMLMessage({
                content: t('welcomeMessage', {
                  escapeValue: false,
                  categoryName: ui.category.id ? t(ui.category.name) : null
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );
            chat.addMessage(
              new HTMLMessage({
                content: t('projectSelector.welcomeMessage', {
                  escapeValue: false
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );
            chat.addMessage(
              new HTMLMessage({
                content: t('projectSelector.selectorMessage', {
                  escapeValue: false,
                  projectID1: projectSelector.projectID1,
                  projectID2: projectSelector.projectID2
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );

            const triggerNode1 = document.getElementById(projectSelector.projectID1);
            const triggerNode2 = document.getElementById(projectSelector.projectID2);
            if (triggerNode1 !== null && triggerNode2 !== null) {
              triggerNode1.addEventListener('click', evt =>
                handleStart(1, projectSelector.projectID1, projectSelector.source1, evt)
              );
              triggerNode2.addEventListener('click', evt =>
                handleStart(2, projectSelector.projectID2, projectSelector.source2, evt)
              );
            }
          } else if (window.location.href.toLowerCase().indexOf(projectSelector.detectURL1) > 0) {
            chat.addMessage(
              new HTMLMessage({
                content: t('projectSelector.subMessage1', {
                  escapeValue: false
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );
          } else if (window.location.href.toLowerCase().indexOf(projectSelector.detectURL2) > 0) {
            chat.addMessage(
              new HTMLMessage({
                content: t('projectSelector.subMessage2', {
                  escapeValue: false
                }),
                type: MESSAGE_TYPE_RECEIVED
              })
            );
          }
        } else {
          chat.addMessage(
            new HTMLMessage({
              content: t('welcomeMessage', {
                escapeValue: false,
                categoryName: ui.category.id ? t(ui.category.name) : null
              }),
              type: MESSAGE_TYPE_RECEIVED
            })
          );
        }

        isSent.current = true;
        chat.isWelcomeMessage = true;
      }, welcomeMessage.timeout);
    }

    if (chatWindow.isClosed) {
      isSent.current = false;
      chat.isWelcomeMessage = false;
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    chat,
    chatWindow.isOpened,
    chatWindow.isCookie,
    network.isOnline,
    network.isSlowConnection,
    t,
    chatWindow.isClosed,
    welcomeMessage,
    isForced,
    ui.category.id,
    ui.category.name,
    ui.isStartChatWithoutCategory,
    intentOnInit,
    headerMenu,
    projectSelector,
    app.isReadyLanguage,
    ui,
    chatWindow.isOpenCookie
  ]);
}
